// filenameSanitizer.js - Comprehensive filename and path sanitization
// Prevents Windows/cross-platform filesystem issues

const stripControlCharacters = (value) =>
  Array.from(value).filter(char => {
    const code = char.charCodeAt(0);
    return code >= 32 && code !== 127;
  }).join('');

const hasControlCharacters = (value) =>
  Array.from(value).some(char => {
    const code = char.charCodeAt(0);
    return code < 32 || code === 127;
  });

/**
 * Sanitizes a filename or path component to be safe across all operating systems
 * @param {string} input - The input filename or path component
 * @param {object} options - Sanitization options
 * @returns {string} - Sanitized filename
 */
export const sanitizeFilename = (input, options = {}) => {
  if (!input || typeof input !== 'string') {
    return 'untitled';
  }

  const {
    maxLength = Infinity, // No length limit
    replacement = '_', // Character to replace invalid chars with
    preserveExtension = true, // Keep file extensions intact
    allowSpaces = false, // Convert spaces to underscores by default
    preserveHyphens = false, // Keep hyphens in language codes
  } = options;

  let sanitized = input.trim();

  // Extract extension if preserving it
  let extension = '';
  if (preserveExtension) {
    const lastDotIndex = sanitized.lastIndexOf('.');
    if (lastDotIndex > 0 && lastDotIndex < sanitized.length - 1) {
      extension = sanitized.substring(lastDotIndex);
      sanitized = sanitized.substring(0, lastDotIndex);
    }
  }

  // Remove or replace problematic characters
  sanitized = sanitized
    // Remove Windows/cross-platform forbidden characters: < > : " | ? * / \ & , ( ) and single quotes '
    .replace(/[<>:'"|?*/\\&,()]/g, replacement)
    // Remove control characters (0-31, 127)
    .replace(/./g, char => stripControlCharacters(char))
    // Handle ligatures and special characters that don't decompose with NFD
    .replace(/[œŒæÆßĸ]/g, '') // Remove ligatures entirely
    // Remove accented characters (normalize and remove diacritics)
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    // Replace spaces and optionally hyphens with replacement character
    .replace(preserveHyphens ? /\s+/g : /[\s-]+/g, allowSpaces ? ' ' : replacement)
    // Remove leading/trailing dots and spaces (Windows issues)
    .replace(/^[.\s]+|[.\s]+$/g, '')
    // Replace multiple replacement characters with single one
    .replace(new RegExp(`\\${replacement}+`, 'g'), replacement)
    // Remove leading/trailing replacement characters
    .replace(new RegExp(`^\\${replacement}+|\\${replacement}+$`, 'g', ''));

  // Handle Windows reserved names (CON, PRN, AUX, NUL, COM1-9, LPT1-9)
  const windowsReserved = /^(CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])$/i;
  if (windowsReserved.test(sanitized)) {
    sanitized = `${sanitized}${replacement}file`;
  }

  // Ensure minimum length
  if (sanitized.length === 0) {
    sanitized = 'untitled';
  }

  // Handle maximum length (accounting for extension)
  if (maxLength !== Infinity) {
    const maxBaseLength = maxLength - extension.length;
    if (sanitized.length > maxBaseLength) {
      sanitized = sanitized.substring(0, maxBaseLength);
      // Remove trailing replacement character if truncation created one
      sanitized = sanitized.replace(new RegExp(`\\${replacement}+$`), '');
    }
  }

  // Reattach extension
  return sanitized + extension;
};

/**
 * Sanitizes a full path, handling each component separately
 * @param {string} path - The full path to sanitize
 * @param {object} options - Sanitization options
 * @returns {string} - Sanitized path
 */
export const sanitizePath = (path, options = {}) => {
  if (!path || typeof path !== 'string') {
    return 'untitled';
  }

  // Split path into components, sanitize each, then rejoin
  const pathSeparator = path.includes('/') ? '/' : '\\';
  const components = path.split(/[/\\]+/);

  const sanitizedComponents = components.map((component, index) => {
    if (component === '' && index === 0) {
      // Preserve leading slash for absolute paths
      return '';
    }
    if (component === '') {
      // Skip empty components (from double slashes)
      return null;
    }

    // For the last component, preserve extension by default
    const isLastComponent = index === components.length - 1;
    const componentOptions = {
      ...options,
      preserveExtension: isLastComponent && (options.preserveExtension !== false)
    };

    return sanitizeFilename(component, componentOptions);
  }).filter(component => component !== null);

  return sanitizedComponents.join(pathSeparator);
};

/**
 * Helper function to format language code consistently
 * @param {string} code - Language code
 * @returns {string} - Formatted language code
 */
const formatLanguageCode = (code) => {
  if (!code) return 'en-us';
  const parts = code.split(/[-_]/);
  if (parts.length === 1) {
    const lang = parts[0].toLowerCase();
    return (lang === 'en') ? 'en-us' : `${lang}-${lang}`;
  } else {
    return `${parts[0].toLowerCase()}-${parts[1].toLowerCase()}`;
  }
};

/**
 * Generates a safe guide filename based on metadata
 * @param {object} metadata - Guide metadata
 * @returns {string} - Safe filename
 */
export const generateSafeGuideFilename = (metadata) => {
  const {
    country = 'unknown',
    language = 'en-us',
    name = 'guide'
  } = metadata;

  // Format language code properly and sanitize everything else
  const safeCountry = sanitizeFilename(country || 'unknown', { maxLength: Infinity, preserveExtension: false }).toLowerCase();
  const safeLanguage = formatLanguageCode(language).replace(/[^a-z0-9-]/g, ''); // Keep hyphens for filename (same as folder)
  const safeName = sanitizeFilename(name || 'guide', { maxLength: Infinity, preserveExtension: false }).toLowerCase();

  return `${safeCountry}_${safeLanguage}_${safeName}.json`;
};

/**
 * Generates a safe folder name for guide storage
 * @param {object} metadata - Guide metadata
 * @returns {string} - Safe folder name
 */
export const generateSafeFolderName = (metadata) => {
  const {
    country = 'unknown',
    language = 'en-us',
    name = 'guide'
  } = metadata;

  // Create folder name components (no extension) with lowercase conversion
  const safeCountry = sanitizeFilename(country || 'unknown', { maxLength: Infinity, preserveExtension: false }).toLowerCase();
  const safeLanguage = formatLanguageCode(language).replace(/[^a-z0-9-]/g, '');
  const safeName = sanitizeFilename(name || 'guide', { maxLength: Infinity, preserveExtension: false }).toLowerCase();

  return `${safeCountry}_${safeLanguage}_${safeName}`;
};

/**
 * Validates if a filename would cause issues
 * @param {string} filename - Filename to validate
 * @returns {object} - Validation result with issues array
 */
export const validateFilename = (filename) => {
  const issues = [];

  if (!filename || typeof filename !== 'string') {
    issues.push('Filename is empty or invalid');
    return { isValid: false, issues };
  }

  // Check for forbidden characters
  if (/[<>:"|?*/\\]/.test(filename)) {
    issues.push('Contains forbidden characters: < > : " | ? * / \\');
  }

  // Check for control characters
  if (hasControlCharacters(filename)) {
    issues.push('Contains control characters');
  }

  // Check for Windows reserved names
  const nameWithoutExt = filename.replace(/\.[^.]*$/, '');
  if (/^(CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])$/i.test(nameWithoutExt)) {
    issues.push('Uses Windows reserved name');
  }

  // Check length
  if (filename.length > 255) {
    issues.push('Filename too long (over 255 characters)');
  }

  // Check for leading/trailing problematic characters
  if (/^[.\s]|[.\s]$/.test(filename)) {
    issues.push('Starts or ends with dot or space');
  }

  return {
    isValid: issues.length === 0,
    issues
  };
};

/**
 * Example usage and test cases
 */
export const testSanitization = () => {
  const testCases = [
    'casa_de_fresco:_the_coolest_room_in_the_house',
    'the_formal_dining:_where_east_meets_west_for_dinner',
    'north_laine:_bohemia_by_the_sea',
    'regency_square:_aristocratic_real_estate_scheme_disguised_as_urbanism',
    'the_lanes:_medieval_maze_meets_modern_merchants',
    "ZURICH'S_BUNKERS:_WHERE_SWITZERLAND_PREPARED_FOR_THE_WORST_en-US_google_en-US-Studio-O_rate1_1744453673047.mp3",
    'CON.txt',
    'file<with>bad:chars"here|now.json',
    'file   with   lots   of   spaces.mp3',
    '.hidden_file_with_leading_dot',
    'file_ending_with_dot.',
    'a'.repeat(300) + '.json',
    "A tour do 'O meu Airbnb está a matar os bairros'"
  ];

  console.log('🧪 Filename Sanitization Test Results:');
  console.log('=====================================');

  testCases.forEach(testCase => {
    const sanitized = sanitizeFilename(testCase);
    const validation = validateFilename(testCase);

    console.log(`Original: "${testCase}"`);
    console.log(`Sanitized: "${sanitized}"`);
    console.log(`Issues: ${validation.issues.join(', ') || 'None'}`);
    console.log('---');
  });
};

export default {
  sanitizeFilename,
  sanitizePath,
  generateSafeGuideFilename,
  generateSafeFolderName,
  validateFilename,
  testSanitization
};
